import { Button } from '@app/ui';
import { Link, useRouter } from '@tanstack/react-router';

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return 'An unexpected error occurred';
}

/**
 * Heuristic: when the failing message looks like an auth/session error,
 * surface a "Clear session" recovery link so the user isn't stuck behind
 * a bad cookie they can't see or remove.
 */
function looksLikeAuthError(message: string): boolean {
  const m = message.toLowerCase();
  return (
    m.includes('decrypt') ||
    m.includes('jwks') ||
    m.includes('private key') ||
    m.includes('session') ||
    m.includes('unauthorized')
  );
}

export function RouteErrorComponent({ error }: { error: unknown }) {
  const router = useRouter();
  const message = getErrorMessage(error);
  const showRecovery = looksLikeAuthError(message);
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <h2 className="text-xl font-semibold mb-2">Something went wrong</h2>
      <p className="text-muted-foreground text-sm mb-4 max-w-md text-center">{message}</p>
      <div className="flex flex-wrap justify-center gap-3">
        <Button variant="outline" onClick={() => router.invalidate()}>
          Try Again
        </Button>
        <Link to="/">
          <Button variant="ghost">Go Home</Button>
        </Link>
        {showRecovery && (
          <Link to="/auth/recover">
            <Button variant="ghost">Clear session</Button>
          </Link>
        )}
      </div>
    </div>
  );
}
